import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail } from "lucide-react";

/**
 * ReplyToField — controlled email input used inside any "send to customer"
 * dialog (invoice send, payment receipt, customer statement).
 *
 * Defaults to the current user's email so customer replies land at the
 * person who actually clicked "Send", rather than always at the org's
 * generic contact_email. Falls back to the org contact email if the user
 * doesn't have an email on file (rare, but possible for SSO accounts).
 *
 * Pass `value === undefined` to use the default; otherwise pass a string.
 */
export default function ReplyToField({ value, onChange, testId = "reply-to" }) {
    const { user, currentOrg } = useAuth();
    const userEmail = user?.email || "";
    const orgEmail = currentOrg?.contact_email || "";
    const placeholder = userEmail || orgEmail || "you@yourcompany.com";

    return (
        <div>
            <Label className="flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-secondary-dark" />
                Reply-to address
            </Label>
            <Input
                type="email"
                value={value ?? userEmail}
                onChange={(e) => onChange?.(e.target.value)}
                placeholder={placeholder}
                className="mt-1.5"
                data-testid={testId}
            />
            <p className="text-xs text-muted-foreground mt-1.5">
                Customer replies go here. Defaults to your account email
                {orgEmail && userEmail !== orgEmail && (
                    <> — leave blank to fall back to the org address (<span className="font-mono">{orgEmail}</span>)</>
                )}
                .
            </p>
        </div>
    );
}
